import { type ReactNode, useEffect, useState } from 'react'

import { configService } from '@/api/services'
import useRegisterLoadingState from '@/hooks/useRegisterLoadingState'

import type { Config } from '../types'
import { ConfigContext } from './ConfigContext'

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<Config | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Register loading state with global loading system
  useRegisterLoadingState('config', loading)

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await configService.getConfig()
        if (!data) {
          throw new Error('config not found')
        }

        // configService already returns the flattened structure
        setConfig(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to fetch config')
      } finally {
        setLoading(false)
      }
    }
    fetchConfig()
  }, [])

  return (
    <ConfigContext.Provider value={{ config, loading, error }}>
      {error ? (
        <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900 text-red-600 dark:text-red-400">
          Error loading config: {error}
        </div>
      ) : (
        children
      )}
    </ConfigContext.Provider>
  )
}
